import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import API from '../../services/api';
import {
  LayoutDashboard, Users, Briefcase, UserCheck,
  Building2, ClipboardList, CalendarCheck, CreditCard,
  FileText, Bell, LogOut, Menu, X, ChevronRight, Shield, DollarSign, Leaf, TableProperties, FileCheck, Receipt, BookOpen, CalendarDays, HardDrive, KeyRound
} from 'lucide-react';

const allNavItems = [
  { label: 'Dashboard',        path: '/dashboard',         icon: LayoutDashboard, roles: ['super_admin','branch_manager','recruiter','finance','client','candidate'] },
  { label: 'Clients',          path: '/clients',           icon: Building2,       roles: ['super_admin','branch_manager','recruiter','finance'] },
  { label: 'Candidates',       path: '/candidates',        icon: Users,           roles: ['super_admin','branch_manager','recruiter'] },
  { label: 'Jobs',             path: '/jobs',              icon: Briefcase,       roles: ['super_admin','branch_manager','recruiter','client'] },
  { label: 'Deployments',      path: '/deployments',       icon: UserCheck,       roles: ['super_admin','branch_manager','recruiter','finance','client','candidate'] },
  { label: 'Attendance',       path: '/attendance',        icon: CalendarCheck,   roles: ['super_admin','branch_manager','recruiter','candidate'] },
  { label: 'Salary Structure', path: '/salary-components', icon: DollarSign,      roles: ['super_admin','branch_manager','finance'] },
  { label: 'Leave Setup',        path: '/leave-setup',          icon: Leaf,             roles: ['super_admin','branch_manager'] },
  { label: 'Att. Register',     path: '/attendance-register',  icon: TableProperties,  roles: ['super_admin','branch_manager','finance'] },
  { label: 'Payroll',          path: '/payroll',           icon: CreditCard,      roles: ['super_admin','branch_manager','finance','candidate'] },
  { label: 'Statutory',        path: '/statutory-compliance', icon: FileCheck,     roles: ['super_admin','branch_manager','finance'] },
  { label: 'Reimbursements',   path: '/reimbursements',       icon: Receipt,       roles: ['super_admin','branch_manager','finance'] },
  { label: 'Payroll Register', path: '/payroll-register',     icon: BookOpen,      roles: ['super_admin','branch_manager','finance'] },
  { label: 'Annual Payroll',   path: '/annual-payroll',       icon: CalendarDays,  roles: ['super_admin','branch_manager','finance'] },
  { label: 'Invoices',         path: '/invoices',          icon: FileText,        roles: ['super_admin','branch_manager','finance','client'] },
  { label: 'Reports',          path: '/reports',           icon: ClipboardList,   roles: ['super_admin','branch_manager','finance'] },
  { label: 'Users',            path: '/users',             icon: Shield,          roles: ['super_admin','branch_manager'] },
  { label: 'Backup & Restore', path: '/backup',            icon: HardDrive,       roles: ['super_admin'] },
];

// ── Change Password Modal ─────────────────
const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      return toast.error('New passwords do not match');
    }
    if (form.new_password.length < 6) {
      return toast.error('New password must be at least 6 characters');
    }
    setLoading(true);
    try {
      await API.post('/auth/change-password', {
        currentPassword: form.current_password,
        newPassword    : form.new_password
      });
      toast.success('Password changed successfully');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Change Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[
            { label: 'Current Password', field: 'current_password' },
            { label: 'New Password',     field: 'new_password'     },
            { label: 'Confirm New Password', field: 'confirm_password' }
          ].map(({ label, field }) => (
            <div key={field}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <input type="password" required value={form[field]}
                onChange={e => setForm({ ...form, [field]: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••" />
            </div>
          ))}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen,   setSidebarOpen]   = useState(true);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const { user, logoutUser }              = useAuth();
  const navItems = allNavItems.filter(item => item.roles.includes(user?.role));
  const location                      = useLocation();
  const navigate                      = useNavigate();

  const handleLogout = () => {
    logoutUser();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} bg-white border-r border-gray-100 flex flex-col transition-all duration-200`}>

        {/* Logo */}
        <div className="h-14 flex items-center px-4 border-b border-gray-100">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">M</span>
          </div>
          {sidebarOpen && (
            <span className="ml-3 font-semibold text-gray-900 text-sm truncate">
              Manpower
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map(({ label, path, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center px-4 py-2.5 mx-2 rounded-lg mb-0.5 text-sm transition
                  ${active
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {sidebarOpen && <span className="ml-3">{label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-gray-100">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-xs font-semibold">
                  {user?.name?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.role}</p>
              </div>
              <button onClick={() => setShowChangePwd(true)} className="text-gray-400 hover:text-blue-500" title="Change Password">
                <KeyRound size={15} />
              </button>
              <button onClick={handleLogout} className="text-gray-400 hover:text-red-500" title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="w-full flex justify-center text-gray-400 hover:text-red-500">
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className="h-14 bg-white border-b border-gray-100 flex items-center px-4 gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-gray-600"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span>Home</span>
            <ChevronRight size={14} />
            <span className="text-gray-900 font-medium capitalize">
              {location.pathname.replace('/', '')}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <Link to="/notifications" className="text-gray-400 hover:text-gray-600 relative">
              <Bell size={20} />
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
    </div>
  );
};

export default Layout;